import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Modal, Table, Tag, Typography, message } from 'antd'
import { getVibeProjectCodeMembers, revokeVibeProjectCodePermission } from '../../../request'
import { formatDateTime } from '../terminalModel'

const { Text } = Typography

// 成员角色/授权来源文案与个人中心 Vibe 详情保持一致（Profile.js getCodeMemberRoleText / getCodeMemberSourceText）。
function getMemberRoleText (accessLevel) {
  const text = String(accessLevel || '').trim()
  if (!text) return 'Maintainer'
  if (text === '40' || text.toLowerCase() === 'maintainer') return 'Maintainer'
  if (text === '30' || text.toLowerCase() === 'developer') return 'Developer'
  return text
}

function getMemberSourceText (source) {
  const sourceText = {
    request_approved: '申请审批',
    owner_grant: '负责人授权'
  }[source]
  return sourceText || source || '-'
}

/**
 * 操作 tab「代码仓库成员」分区（仅项目登记人可见）。
 * 数据来自 getVibeProjectCodeMembers；撤销走 revokeVibeProjectCodePermission，二次确认后刷新列表。
 * 独立组件，接口失败只降级本分区，不影响授权/申请/审批等其它分区。
 */
export default function CodeMembersSection ({ projectId, visible = true }) {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [members, setMembers] = useState([])
  const [revokingUserId, setRevokingUserId] = useState(null)
  const mountedRef = useRef(true)
  // 切项目/重复刷新时防止旧请求覆盖新结果
  const requestSeqRef = useRef(0)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const loadMembers = useCallback(() => {
    if (projectId === null || projectId === undefined || projectId === '') return
    const seq = requestSeqRef.current + 1
    requestSeqRef.current = seq
    setLoading(true)
    setLoadError(false)
    getVibeProjectCodeMembers({ projectId })
      .then(result => {
        if (!mountedRef.current || requestSeqRef.current !== seq) return
        setMembers(Array.isArray(result && result.members) ? result.members : [])
        setLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current || requestSeqRef.current !== seq) return
        setMembers([])
        setLoadError(true)
        setLoading(false)
      })
  }, [projectId])

  useEffect(() => {
    if (!visible) return
    loadMembers()
  }, [visible, loadMembers])

  const revokeMember = member => {
    if (revokingUserId || projectId === null || projectId === undefined) return
    const memberName = member.userNick || member.userName || member.gitlabUsername || member.userId || '该成员'
    Modal.confirm({
      title: `撤销 ${memberName} 的代码仓库权限？`,
      content: '撤销后该成员将失去本项目 GitLab 代码仓库的 Maintainer 权限。',
      okText: '确认撤销',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        setRevokingUserId(member.userId)
        return revokeVibeProjectCodePermission({ projectId, userId: member.userId })
          .then(() => {
            message.success('已撤销代码仓库权限')
            loadMembers()
          })
          .catch(error => {
            message.error((error && error.message) || '撤销代码仓库权限失败')
          })
          .finally(() => {
            if (mountedRef.current) setRevokingUserId(null)
          })
      }
    })
  }

  const columns = [
    {
      title: '姓名',
      key: 'userName',
      render: (_, record) => record.userNick || record.userName || record.userId || '-'
    },
    {
      title: 'GitLab 账号',
      key: 'gitlabUsername',
      render: (_, record) => (
        record.gitlabUsername
          ? <Text code>{record.gitlabUsername}</Text>
          : <Text type="secondary">-</Text>
      )
    },
    {
      title: '角色',
      key: 'accessLevel',
      width: 110,
      render: (_, record) => <Tag color="blue">{getMemberRoleText(record.accessLevel)}</Tag>
    },
    {
      title: '授权来源',
      key: 'source',
      width: 110,
      render: (_, record) => getMemberSourceText(record.source)
    },
    {
      title: '授权时间',
      key: 'grantedAt',
      width: 150,
      render: (_, record) => formatDateTime(record.grantedAt)
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          danger
          size="small"
          loading={String(revokingUserId) === String(record.userId)}
          onClick={() => revokeMember(record)}
        >
          撤销
        </Button>
      )
    }
  ]

  return <section className="term-ops-section">
    <div className="term-ops-head">
      <span className="term-ops-title">代码仓库成员</span>
      <span className="term-ops-desc">除项目登记人外，被授权同事会获得本项目 GitLab 代码仓库权限，可随时撤销。</span>
    </div>
    <div className="term-ops-card">
      {loadError
        ? <Alert
          type="error"
          showIcon
          message="代码仓库成员加载失败"
          action={<button type="button" className="term-link" onClick={loadMembers}>重试</button>}
        />
        : <Table
          className="term-ops-table"
          rowKey={record => String(record.userId)}
          size="small"
          loading={loading}
          pagination={false}
          dataSource={members}
          locale={{ emptyText: '暂无协作成员，仅项目登记人可修改代码' }}
          columns={columns}
        />}
    </div>
  </section>
}
